/**
 * Manifest-aware flow helpers.
 *
 * The onboarding mini-app uses `manifest:prevStep` to resolve the
 * previous screen in a manifest-backed step sequence.
 */

import type { FlowStep } from "../types/flow";

import type { ExecutionContext } from "../context/execution-context";
import { parsePath } from "../expressions/path";
import { resolveValue } from "../expressions/resolve";
import { registerStepHandler } from "../runtime/step-registry";

function asRecord(step: FlowStep): Record<string, unknown> {
 return step as unknown as Record<string, unknown>;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
 return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function getManifestDoc(ctx: ExecutionContext): Record<string, unknown> | null {
 const flow = ctx.flow as unknown as Record<string, unknown>;
 const meta = isPlainObject(flow.meta) ? (flow.meta as Record<string, unknown>) : null;

 const manifestFromMeta = meta?.manifest;
 if (isPlainObject(manifestFromMeta) && Array.isArray(manifestFromMeta.steps)) {
 return manifestFromMeta;
 }

 const manifestTopLevel = flow.manifest;
 if (isPlainObject(manifestTopLevel) && Array.isArray(manifestTopLevel.steps)) {
 return manifestTopLevel;
 }

 return null;
}

registerStepHandler("manifest:prevStep", (step, ctx) => {
 const r = asRecord(step);

 const currentIdValue = resolveValue(r["currentId"] ?? r["currentStepId"], ctx);
 const currentId =
 typeof currentIdValue === "string" ? currentIdValue.trim() : "";

 const outputValue = resolveValue(r["output"] ?? r["out"], ctx);
 const outputPath =
 typeof outputValue === "string" ? outputValue.trim() : "";

 if (!outputPath) return;

 const parsedOutput = parsePath(outputPath);
 if (!parsedOutput) return;

 const manifest = getManifestDoc(ctx);
 if (!manifest || !currentId) {
 ctx.writeState(parsedOutput.scope, parsedOutput.segments, null);
 return;
 }

 const steps = Array.isArray(manifest.steps) ? manifest.steps : [];
 const stepList = steps.filter(isPlainObject);

 const currentIndex = stepList.findIndex(
 (candidate) => String(candidate.id ?? "").trim() === currentId,
 );

 const previousStep =
 currentIndex >0 ? stepList[currentIndex -1] : undefined;

 ctx.writeState(
 parsedOutput.scope,
 parsedOutput.segments,
 previousStep ? { ...previousStep } : null,
 );
});